/**
 * UI preferences storage (date range, excluded models, etc.).
 */

// Key for UI preferences
const UI_PREFERENCES_KEY = 'ui_preferences';

export interface UIPreferences {
  lastDateRange?: string;
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
  excludedModels?: string[];
}

const DEFAULT_UI_PREFERENCES: UIPreferences = {
  lastDateRange: '1day',
  excludedModels: ['gpt-4o-mini-search-preview-2025-03-11', 'gpt-4.1-mini-2025-04-14', 'gpt-4.1-nano-2025-04-14']
};

/**
 * Save UI preferences
 */
export const saveUIPreferences = (preferences: UIPreferences): boolean => {
  try {
    localStorage.setItem(UI_PREFERENCES_KEY, JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Failed to save UI preferences:', error);
    return false;
  }
};

/**
 * Get UI preferences
 */
export const getUIPreferences = (): UIPreferences => {
  try {
    const stored = localStorage.getItem(UI_PREFERENCES_KEY);
    if (!stored) return DEFAULT_UI_PREFERENCES;

    const preferences = JSON.parse(stored);
    return {
      lastDateRange: preferences.lastDateRange ?? DEFAULT_UI_PREFERENCES.lastDateRange,
      customDateRange: preferences.customDateRange,
      excludedModels: preferences.excludedModels ?? DEFAULT_UI_PREFERENCES.excludedModels
    };
  } catch (error) {
    console.error('Failed to get UI preferences:', error);
    return DEFAULT_UI_PREFERENCES;
  }
};

/**
 * Save last date range selection
 */
export const saveLastDateRange = (dateRange: string, customDateRange?: { startDate: string; endDate: string }): boolean => {
  const preferences = getUIPreferences();
  const updatedPreferences: UIPreferences = {
    ...preferences,
    lastDateRange: dateRange,
    customDateRange: dateRange === 'custom' ? customDateRange : undefined
  };
  return saveUIPreferences(updatedPreferences);
};

/**
 * Get last date range selection
 */
export const getLastDateRange = (): { dateRange: string; customDateRange?: { startDate: string; endDate: string } } => {
  const preferences = getUIPreferences();
  return {
    dateRange: preferences.lastDateRange || '1day',
    customDateRange: preferences.customDateRange
  };
};

/**
 * Save excluded models
 */
export const saveExcludedModels = (models: string[]): boolean => {
  const preferences = getUIPreferences();
  const updatedPreferences: UIPreferences = {
    ...preferences,
    excludedModels: models
  };
  return saveUIPreferences(updatedPreferences);
};

/**
 * Get excluded models
 */
export const getExcludedModels = (): string[] => {
  const preferences = getUIPreferences();
  return preferences.excludedModels || DEFAULT_UI_PREFERENCES.excludedModels || [];
};
